import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { apiGet, apiPost, ApiClientError } from '@client/lib/api';
import { formatCents } from '@shared/money';
import styles from './PublicQuotePage.module.css';

interface PublicItem {
  description: string;
  quantity: number;
  unitPriceCents: number;
  lineTotalCents: number;
}

interface BusinessInfo {
  name: string;
  phone: string | null;
  email: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
}

interface PublicQuoteData {
  business: BusinessInfo;
  canRespond: boolean;
  customerLabel: string;
  customerMessage: string | null;
  expirationDate: string | null;
  issueDate: string;
  items: PublicItem[];
  kind: 'QUOTE';
  number: string;
  propertyAddress: string | null;
  status: string;
  subtotalCents: number;
  taxCents: number;
  totalCents: number;
}

/**
 * Public quote page — accessible without authentication.
 *
 * Rendered outside the authenticated route tree so a customer with no account
 * can view their quote.
 */
export function PublicQuotePage() {
  const { token = '' } = useParams<{ token: string }>();
  const [quote, setQuote] = useState<PublicQuoteData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState<boolean>(false);
  const [declining, setDeclining] = useState<boolean>(false);
  const acceptRef = useRef<boolean>(false);
  const declineRef = useRef<boolean>(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const data = await apiGet<PublicQuoteData>(`/public/quotes/${encodeURIComponent(token)}`);
        if (!cancelled) {
          setQuote(data);
        }
      } catch (e) {
        if (!cancelled) {
          if (e instanceof ApiClientError && e.status === 404) {
            setError('not-found');
          } else {
            setError('error');
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleAccept = () => {
    if (accepting) return;
    if (acceptRef.current) return;
    acceptRef.current = true;
    setAccepting(true);

    apiPost<PublicQuoteData>(`/public/quotes/${encodeURIComponent(token)}/accept`)
      .then((result) => {
        setQuote(result);
      })
      .catch(() => {
        setError('error');
      })
      .finally(() => {
        setAccepting(false);
        acceptRef.current = false;
      });
  };

  const handleDecline = () => {
    if (declining) return;
    if (declineRef.current) return;
    declineRef.current = true;
    setDeclining(true);

    apiPost<PublicQuoteData>(`/public/quotes/${encodeURIComponent(token)}/decline`)
      .then((result) => {
        setQuote(result);
      })
      .catch(() => {
        setError('error');
      })
      .finally(() => {
        setDeclining(false);
        declineRef.current = false;
      });
  };

  if (loading) {
    return <div className={styles.loading}>Loading quote…</div>;
  }

  if (error === 'not-found') {
    return (
      <div className={styles.notFound}>
        <h1 className={styles.notFoundTitle}>This link is no longer valid</h1>
        <p className={styles.notFoundText}>Ask the business for a new one.</p>
      </div>
    );
  }

  if (error === 'error') {
    return (
      <div className={styles.notFound}>
        <h1 className={styles.notFoundTitle}>Something went wrong</h1>
        <p className={styles.notFoundText}>Please try again or contact the business directly.</p>
      </div>
    );
  }

  if (!quote) {
    return null;
  }

  const addressParts = [
    quote.business.addressLine1,
    quote.business.addressLine2,
    [quote.business.city, quote.business.state, quote.business.postalCode]
      .filter(Boolean)
      .join(', '),
  ].filter(Boolean);

  const statusLabel = quote.status.charAt(0).toUpperCase() + quote.status.slice(1).toLowerCase();

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Quote {quote.number}</h1>
        <p className={styles.subtitle}>for {quote.customerLabel}</p>
      </div>

      <div className={styles.businessInfo}>
        <p className={styles.businessName}>{quote.business.name}</p>
        {addressParts.length > 0 && (
          <p className={styles.businessAddress}>{addressParts.join(', ')}</p>
        )}
      </div>

      {quote.customerMessage && (
        <div className={styles.customerMessage}>
          <strong>Message:</strong> {quote.customerMessage}
        </div>
      )}

      <div className={styles.status}>{statusLabel}</div>

      <h2 className={styles.sectionTitle}>Line items</h2>
      {quote.items.length === 0 ? (
        <p className={styles.empty}>No line items.</p>
      ) : (
        <ul className={styles.lineItems}>
          {quote.items.map((item, index) => (
            <li key={index} className={styles.lineItem}>
              <span className={styles.lineItemDescription}>{item.description}</span>
              <span className={styles.lineItemDetails}>
                {item.quantity} × {formatCents(item.unitPriceCents)}
              </span>
              <span className={styles.lineItemTotal}>{formatCents(item.lineTotalCents)}</span>
            </li>
          ))}
        </ul>
      )}

      <div className={styles.totals}>
        <div className={styles.totalsRow}>
          <span>Subtotal</span>
          <span>{formatCents(quote.subtotalCents)}</span>
        </div>
        <div className={styles.totalsRow}>
          <span>Tax</span>
          <span>{formatCents(quote.taxCents)}</span>
        </div>
        <div className={styles.totalsRow}>
          <span>Total</span>
          <span>{formatCents(quote.totalCents)}</span>
        </div>
      </div>

      {quote.canRespond && (
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.acceptButton}
            disabled={accepting || declining}
            onClick={handleAccept}
            aria-busy={accepting}
          >
            {accepting ? 'Accepting…' : 'Accept quote'}
          </button>
          <button
            type="button"
            className={styles.declineButton}
            disabled={accepting || declining}
            onClick={handleDecline}
            aria-busy={declining}
          >
            {declining ? 'Declining…' : 'Decline quote'}
          </button>
        </div>
      )}

      {!quote.canRespond && quote.status !== 'SENT' && (
        <div className={styles.status}>This quote has been {quote.status.toLowerCase()}.</div>
      )}

      {quote.expirationDate && <div className={styles.expiry}>Expires: {quote.expirationDate}</div>}
    </div>
  );
}
